// pages/RoleManagement.js - Halaman maintain custom Role (admin only)
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import {
  ShieldCheck, Plus, Edit2, Trash2, Save, X, Lock, MapPin,
  CheckCircle2, AlertTriangle, RefreshCw, Search, Users as UsersIcon,
  Cloud, DollarSign, Ticket, Users, ClipboardList, UserCog, DatabaseZap, Mail,
  MessageSquare, Database, Boxes, ScrollText, MessageCircle, CalendarClock, Sparkles, StickyNote, NotebookText, Code2,
  Activity, Puzzle, Truck, KeyRound, SlidersHorizontal, Zap, AppWindow, Link2, MonitorSmartphone, FileUp, Receipt, CornerDownRight,
} from 'lucide-react';
import { ROUTE_META } from '../routesMeta';
import { ALL_MENUS } from '../components/Sidebar';
import { GROUP_ORDER, GROUP_KEY, groupOf } from '../config/navGroups';

// ── Metadata permission ──────────────────────────────────────────────────────
// Hanya TAMPILAN: label, ikon, warna. Daftar permission yang sah tetap milik
// backend (AVAILABLE_PERMISSIONS di database.js) dan dikirim lewat
// availablePermissions — berkas ini tidak boleh jadi sumber kebenaran kedua.
// Key yang tidak dikenal tetap muncul dengan gaya generik, bukan disembunyikan:
// permission yang ada di backend tapi tak terlihat di UI adalah akses yang
// tidak bisa dikelola siapa pun.
const PERM_META = {
  // AWS
  aws_billing:        { label: 'AWS Billing',            icon: Cloud,            color: 'text-amber-500',   bgColor: 'bg-amber-50'   },
  aws_cost:           { label: 'AWS Cost Explorer',      icon: DollarSign,       color: 'text-orange-500',  bgColor: 'bg-orange-50'  },
  aws_inventory:      { label: 'AWS Inventory',          icon: Boxes,            color: 'text-cyan-500',    bgColor: 'bg-cyan-50'    },
  aws_cloudtrail:     { label: 'AWS CloudTrail',         icon: ScrollText,       color: 'text-rose-500',    bgColor: 'bg-rose-50'    },
  // SAP
  solman:             { label: 'SAP Solman',             icon: Ticket,           color: 'text-brand-500',   bgColor: 'bg-brand-50'   },
  sap_usage:          { label: 'SAP User Utilization',   icon: Users,            color: 'text-emerald-500', bgColor: 'bg-emerald-50' },
  sap_user_mgmt:      { label: 'SAP User Maintenance',   icon: KeyRound,         color: 'text-violet-500',  bgColor: 'bg-violet-50'  },
  sap_roles:          { label: 'SAP Role Maintenance — Read-only', icon: ShieldCheck, color: 'text-fuchsia-500', bgColor: 'bg-fuchsia-50' },
  sap_roles_write:    { label: 'SAP Role Maintenance — Modify (assign / edit auth / generate)', icon: ShieldCheck, color: 'text-rose-500', bgColor: 'bg-rose-50' },
  sap_basis_mon:      { label: 'SAP Basis Monitor',      icon: Activity,         color: 'text-teal-500',    bgColor: 'bg-teal-50'    },
  // Ikon sengaja dipilih dari yang SUDAH diimpor di kepala berkas — menambah
  // import lucide baru tidak perlu hanya untuk dua entri tampilan.
  sap_logon_audit:    { label: 'SAP External Access Audit', icon: ShieldCheck,   color: 'text-rose-500',    bgColor: 'bg-rose-50'    },
  sap_router_log:     { label: 'SAProuter Log',          icon: ScrollText,       color: 'text-rose-500',    bgColor: 'bg-rose-50'    },
  sap_customizing:    { label: 'SAP Customizing',        icon: Puzzle,           color: 'text-indigo-500',  bgColor: 'bg-indigo-50'  },
  sap_source:         { label: 'ABAP Source Viewer',     icon: Code2,            color: 'text-sky-500',     bgColor: 'bg-sky-50'     },
  sap_table_peek:     { label: 'SAP Table Peek',         icon: DatabaseZap,      color: 'text-rose-500',    bgColor: 'bg-rose-50'    },
  transport:          { label: 'SAP Transport Request',  icon: Truck,            color: 'text-lime-600',    bgColor: 'bg-lime-50'    },
  transport_import:   { label: 'Transport — Import',     icon: Truck,            color: 'text-rose-500',    bgColor: 'bg-rose-50'    },
  // Infra
  m365_license:       { label: 'Microsoft 365 License',  icon: AppWindow,        color: 'text-sky-500',     bgColor: 'bg-sky-50'     },
  m365_pricing_edit:  { label: 'M365 — Ubah Harga',      icon: Receipt,          color: 'text-rose-500',    bgColor: 'bg-rose-50'    },
  sdp:                { label: 'ManageEngine SDP Cloud', icon: Ticket,           color: 'text-orange-500',  bgColor: 'bg-orange-50'  },
  // HR
  employee_overtime:  { label: 'Employee Overtime',      icon: CalendarClock,    color: 'text-amber-500',   bgColor: 'bg-amber-50'   },
  // AI
  ai_chat:            { label: 'AI Chat (panel)',        icon: MessageSquare,    color: 'text-indigo-500',  bgColor: 'bg-indigo-50'  },
  ai_workspace:       { label: 'AI Workspace',           icon: Sparkles,         color: 'text-indigo-500',  bgColor: 'bg-indigo-50'  },
  ai_coding:          { label: 'AI Coding',              icon: Code2,            color: 'text-emerald-500', bgColor: 'bg-emerald-50' },
  ai_actions:         { label: 'AI Actions',             icon: Zap,              color: 'text-violet-500',  bgColor: 'bg-violet-50'  },
  ai_history:         { label: 'AI Chat History',        icon: MessageSquare,    color: 'text-indigo-500',  bgColor: 'bg-indigo-50'  },
  ai_config:          { label: 'AI Data Sources',        icon: Database,         color: 'text-brand-500',   bgColor: 'bg-brand-50'   },
  ai_settings:        { label: 'AI Configuration',       icon: SlidersHorizontal,color: 'text-indigo-500',  bgColor: 'bg-indigo-50'  },
  // Akses & pengguna
  users:              { label: 'User Management',        icon: UserCog,          color: 'text-violet-500',  bgColor: 'bg-violet-50'  },
  role_management:    { label: 'Role Management',        icon: ShieldCheck,      color: 'text-indigo-500',  bgColor: 'bg-indigo-50'  },
  share_links:        { label: 'Share Links',            icon: Link2,            color: 'text-teal-500',    bgColor: 'bg-teal-50'    },
  sessions:           { label: 'Session Log',            icon: MonitorSmartphone,color: 'text-emerald-500', bgColor: 'bg-emerald-50' },
  // Sistem
  logs:               { label: 'Activity Log',           icon: ClipboardList,    color: 'text-slate-500',   bgColor: 'bg-slate-50'   },
  table_maintenance:  { label: 'Table Maintenance',      icon: DatabaseZap,      color: 'text-rose-500',    bgColor: 'bg-rose-50'    },
  monthly_report:     { label: 'Monthly Report',         icon: Mail,             color: 'text-orange-500',  bgColor: 'bg-orange-50'  },
  file_deploy:        { label: 'File Deploy',            icon: FileUp,           color: 'text-indigo-500',  bgColor: 'bg-indigo-50'  },
  // Integrasi
  telegram:           { label: 'Telegram Bot',           icon: MessageCircle,    color: 'text-sky-500',     bgColor: 'bg-sky-50'     },
  telegram_log:       { label: 'Telegram Chat Log',      icon: MessageSquare,    color: 'text-sky-600',     bgColor: 'bg-sky-50'     },
  // Tools
  notes:              { label: 'Notes',                  icon: StickyNote,       color: 'text-yellow-500',  bgColor: 'bg-yellow-50'  },
  notes_plain:        { label: 'Notes Plain',            icon: NotebookText,     color: 'text-lime-600',    bgColor: 'bg-lime-50'    },
};

// Sub-izin: permission yang memperluas permission lain, bukan membuka halaman
// sendiri. Ditandai supaya tidak terbaca sebagai halaman terpisah — mencentang
// 'transport_import' tanpa 'transport' tidak ada gunanya.
const PERM_PARENT = {
  transport_import:  'transport',
  m365_pricing_edit: 'm365_license',
  sap_roles_write:   'sap_roles',
};

// Grup permission DITURUNKAN dari route: permission yang menggerbangi sebuah
// halaman mewarisi grup halaman itu (config/navGroups). Dengan begitu panel ini
// tidak pernah menyimpang dari sidebar/Overview walau halaman berpindah grup.
const PERM_GROUP = {};
ROUTE_META.forEach(r => {
  if (!r.permission || r.permission === '__admin__') return;
  if (!PERM_GROUP[r.permission]) PERM_GROUP[r.permission] = groupOf(r.key);
});
// Permission yang tidak menggerbangi halaman mana pun: sub-izin mengikuti
// induknya, sisanya diatur manual.
Object.entries(PERM_PARENT).forEach(([child, parent]) => {
  if (!PERM_GROUP[child] && PERM_GROUP[parent]) PERM_GROUP[child] = PERM_GROUP[parent];
});
PERM_GROUP.sap_roles_write = PERM_GROUP.sap_roles || 'SAP';
PERM_GROUP.notes           = PERM_GROUP.notes || 'TOOLS';   // route notes: permission null
PERM_GROUP.ai_chat         = PERM_GROUP.ai_workspace || 'AI';  // panel AI Chat: tanpa route, ikut grup AI
const FALLBACK_PERM_GROUP  = 'TOOLS';
// [Site scope] batas data per site untuk SDP Cloud + Network Topology.
// Tidak dicentang satu pun = semua site; dicentang >=1 = hanya site tsb.
const SITE_SCOPE_META = {
  site_medan:   'Site: Medan (PMI + PPI + OSI)',
  site_dumai:   'Site: Dumai (Pacific Indopalm Industries)',
  site_kijing:  'Site: Kijing / Mempawah (Pacific Bio Industry)',
  site_jakarta: 'Site: Jakarta (Pacific Indomas)',
};
Object.entries(SITE_SCOPE_META).forEach(([k, label]) => {
  PERM_META[k] = { label, icon: MapPin, color: 'text-orange-500', bgColor: 'bg-orange-50' };
  PERM_GROUP[k] = 'SITE SCOPE (SDP & Topology)';
});

const PERMISSION_LABEL = {};
Object.entries(PERM_META).forEach(([k, m]) => { PERMISSION_LABEL[k] = m.label; });

// Ubah key permission jadi label rapi otomatis bila tak dikenal:
// 'employee_overtime' → 'Employee Overtime'
const prettyLabel = (key) =>
  routeLabelOf(key) || (menuForPerm(key) || {}).fallback ||
  String(key).split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

// Susun grup secara DINAMIS dari daftar permission backend (availablePerms).
// Urutan grup = GROUP_ORDER (sama dengan sidebar); di dalam grup, sub-izin
// diletakkan tepat setelah induknya.
// Izin yang belum ada di PERM_META → label/ikon dari ROUTE_META dan menu sidebar.
const menuForPerm  = (perm) => ALL_MENUS.find(m => m.permission === perm) || ALL_MENUS.find(m => m.key === perm);
const routeLabelOf = (perm) => (ROUTE_META.find(r => r.permission === perm) || {}).label;
function buildGroups(availablePerms) {
  const avail = Array.isArray(availablePerms) ? availablePerms : [];
  const byGroup = {};

  avail.forEach(key => {
    const g = PERM_GROUP[key] || FALLBACK_PERM_GROUP;
    const meta = PERM_META[key];
    const mnu  = meta ? null : menuForPerm(key);
    (byGroup[g] = byGroup[g] || []).push({
      key,
      label:   meta?.label   || routeLabelOf(key) || mnu?.fallback || prettyLabel(key),
      icon:    meta?.icon    || mnu?.icon || Lock,
      color:   meta?.color   || mnu?.color || 'text-slate-500',
      bgColor: meta?.bgColor || mnu?.bgColor || 'bg-slate-50',
      parent:  PERM_PARENT[key] || null,
    });
  });

  // Sub-izin menempel di bawah induknya; sisanya menjaga urutan backend.
  Object.values(byGroup).forEach(items => {
    const children = items.filter(i => i.parent);
    children.forEach(child => {
      const from = items.indexOf(child);
      const to   = items.findIndex(i => i.key === child.parent);
      if (to >= 0 && to !== from - 1) {
        items.splice(from, 1);
        items.splice(items.findIndex(i => i.key === child.parent) + 1, 0, child);
      }
    });
  });

  return GROUP_ORDER
    .filter(g => byGroup[g]?.length)
    .map(g => ({ group: g, groupKey: GROUP_KEY[g], items: byGroup[g] }))
    // Grup yang tidak ada di GROUP_ORDER (mis. FALLBACK yang berbeda) tetap ikut.
    .concat(Object.keys(byGroup)
      .filter(g => !GROUP_ORDER.includes(g))
      .map(g => ({ group: g, groupKey: null, items: byGroup[g] })));
}

function formatDate(str, lang) {
  if (!str) return '—';
  try {
    return new Date(str.replace(' ', 'T') + 'Z').toLocaleString(lang === 'en' ? 'en-US' : 'id-ID', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return str; }
}

// ── Modal: Create/Edit Role ───────────────────────────────────────────────────
function RoleModal({ role, availablePerms, onSave, onClose }) {
  const GROUPS = buildGroups(availablePerms);
  const { t } = useLang();
  const isEdit = !!role;
  const [form, setForm] = useState({
    name:        role?.name        || '',
    description: role?.description || '',
    permissions: role?.permissions || [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  const togglePerm = (key) => {
    setForm(p => ({
      ...p,
      permissions: p.permissions.includes(key)
        ? p.permissions.filter(x => x !== key)
        : [...p.permissions, key],
    }));
  };

  const toggleGroup = (groupItems) => {
    const groupKeys = groupItems.map(i => i.key);
    const allSelected = groupKeys.every(k => form.permissions.includes(k));
    setForm(p => ({
      ...p,
      permissions: allSelected
        ? p.permissions.filter(k => !groupKeys.includes(k))
        : [...new Set([...p.permissions, ...groupKeys])],
    }));
  };

  const validate = () => {
    if (!isEdit) {
      if (!form.name.trim()) return t('rm.errNameReq');
      if (!/^[A-Za-z0-9_\-]{2,30}$/.test(form.name.trim()))
        return t('rm.errNameFormat');
    }
    if (form.permissions.length === 0)
      return t('rm.errPermReq');
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setSaving(true); setError(null);
    try {
      if (isEdit) {
        await api.put(`/api/roles/${role.id}`, {
          description: form.description,
          permissions: form.permissions,
        });
      } else {
        await api.post('/api/roles', {
          name:        form.name.trim(),
          description: form.description,
          permissions: form.permissions,
        });
      }
      onSave();
    } catch (e) {
      setError(e.response?.data?.error || t('rm.errSave'));
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/40 z-[1000] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl border border-slate-100 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
              <ShieldCheck style={{ width: 18, height: 18 }} className="text-indigo-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800">{isEdit ? t('rm.editRole') : t('rm.newRole')}</h3>
              <p className="text-xs text-slate-400 mt-0.5">
                {isEdit ? t('rm.editing', { name: role.name }) : t('rm.subtitleNew')}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors">
            <X style={{ width: 16, height: 16 }} className="text-slate-500" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5 overflow-y-auto">
          {/* Nama Role */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              {t('rm.roleName')} {!isEdit && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              disabled={isEdit}
              placeholder={t('rm.namePlaceholder')}
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm outline-none
                focus:border-brand-400 focus:ring-2 focus:ring-brand-100
                disabled:bg-slate-50 disabled:text-slate-400"
            />
            <p className="text-xs text-slate-400 mt-1">
              {isEdit
                ? t('rm.nameLocked')
                : t('rm.nameHint')}
            </p>
          </div>

          {/* Deskripsi */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('rm.description')}</label>
            <input
              type="text"
              value={form.description}
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
              placeholder={t('rm.descPlaceholder')}
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm outline-none
                focus:border-brand-400 focus:ring-2 focus:ring-brand-100"
            />
          </div>

          {/* Permissions */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {t('rm.permissions')} <span className="text-xs text-slate-400 font-normal">
                {t('rm.selectedCount', { n: form.permissions.length })}
              </span>
            </label>

            <div className="space-y-4">
              {GROUPS.map(({ group, groupKey, items }) => {
                const groupKeys = items.map(i => i.key);
                const selectedInGroup = groupKeys.filter(k => form.permissions.includes(k)).length;
                const allSelected = selectedInGroup === groupKeys.length;
                return (
                  <div key={group} className="border border-slate-200 rounded-xl overflow-hidden">
                    <div className="flex items-center justify-between bg-slate-50 px-4 py-2.5 border-b border-slate-100">
                      <div className="text-xs font-bold text-slate-600 uppercase tracking-wider">
                        {groupKey && t(groupKey) !== groupKey ? t(groupKey) : group}
                        <span className="ml-2 text-slate-400 font-normal normal-case tracking-normal">
                          ({selectedInGroup}/{groupKeys.length})
                        </span>
                      </div>
                      <button type="button" onClick={() => toggleGroup(items)}
                        className="text-xs font-semibold text-brand-600 hover:text-brand-700">
                        {allSelected ? t('rm.uncheckAll') : t('rm.checkAll')}
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3">
                      {items.map(p => {
                        const Icon = p.icon;
                        const checked = form.permissions.includes(p.key);
                        // t() mengembalikan key-nya sendiri bila terjemahan tidak ada.
                        // Tanpa penjagaan ini, permission tanpa deskripsi menampilkan
                        // teks 'rm.desc.sdp' — terlihat seperti kerusakan.
                        const dKey = 'rm.desc.' + p.key;
                        const desc = t(dKey) === dKey ? p.key : t(dKey);
                        // Sub-izin tanpa induknya tercentang tidak berefek apa pun.
                        // Tetap bisa diklik (urutan mencentang bebas), hanya diberi
                        // peringatan — mematikannya akan menyembunyikan sebabnya.
                        const orphan = p.parent && !form.permissions.includes(p.parent);
                        return (
                          <button key={p.key} type="button" onClick={() => togglePerm(p.key)}
                            className={`flex items-center gap-3 p-3 rounded-xl border transition-all text-left
                              ${p.parent ? 'sm:ml-6' : ''}
                              ${checked
                                ? 'border-brand-500 bg-brand-50/50 ring-2 ring-brand-100'
                                : 'border-slate-200 hover:border-slate-300 bg-white'
                              }`}>
                            {p.parent && (
                              <CornerDownRight style={{ width: 13, height: 13 }}
                                className="flex-shrink-0 -mr-1 text-slate-300" />
                            )}
                            <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${p.bgColor}`}>
                              <Icon className={p.color} style={{ width: 16, height: 16 }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold text-slate-800 leading-tight">{p.label}</div>
                              <div className="text-xs text-slate-400 truncate">{desc}</div>
                              {checked && orphan && (
                                <div className="text-[11px] text-amber-600 mt-0.5 truncate">
                                  {t('rm.needsParent', { parent: PERMISSION_LABEL[p.parent] || p.parent })}
                                </div>
                              )}
                            </div>
                            <div className={`flex-shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center
                              ${checked ? 'bg-brand-600 border-brand-600' : 'border-slate-300 bg-white'}`}>
                              {checked && <CheckCircle2 style={{ width: 13, height: 13 }} className="text-white" />}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-200 rounded-xl">
              <AlertTriangle style={{ width: 14, height: 14 }} className="text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex gap-2">
          <button onClick={onClose}
            className="px-4 py-2 text-sm text-slate-500 hover:bg-slate-50 border border-slate-200 rounded-xl transition-all">
            {t('common.cancel')}
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-60">
            {saving ? <RefreshCw style={{ width: 14, height: 14 }} className="animate-spin" /> : <Save style={{ width: 14, height: 14 }} />}
            {isEdit ? t('rm.saveChanges') : t('rm.createRole')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Main RoleManagement ───────────────────────────────────────────────────────
export default function RoleManagement() {
  const { t, lang } = useLang();
  const [roles, setRoles]     = useState([]);
  const [availablePerms, setAvailablePerms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch]   = useState('');
  const [modal, setModal]     = useState(null);
  const [toast, setToast]     = useState(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Buka modal Create Role otomatis bila datang dari User Management (?create=1)
  useEffect(() => {
    if (searchParams.get('create') === '1') {
      setModal('add');
      searchParams.delete('create');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Buka modal Edit Role otomatis bila datang dari User Management (?edit=<nama role>)
  useEffect(() => {
    const editName = searchParams.get('edit');
    if (!editName || roles.length === 0) return;
    const target = roles.find(r => r.name === editName);
    if (target) setModal({ edit: target });
    searchParams.delete('edit');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, roles]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/roles');
      setRoles(res.data.roles || []);
      setAvailablePerms(res.data.availablePermissions || []);
    } catch (e) {
      showToast(e.response?.data?.error || t('rm.errLoad'), 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  const handleDelete = async (role) => {
    try {
      await api.delete(`/api/roles/${role.id}`);
      showToast(t('rm.roleDeleted', { name: role.name }));
      fetchRoles();
    } catch (e) {
      showToast(e.response?.data?.error || t('rm.errDelete'), 'error');
    } finally {
      setModal(null);
    }
  };

  const filtered = roles.filter(r =>
    !search ||
    r.name.toLowerCase().includes(search.toLowerCase()) ||
    (r.description || '').toLowerCase().includes(search.toLowerCase())
  );

  const totalUsersWithRole = roles.reduce((sum, r) => sum + (r.user_count || 0), 0);
  const customRoleCount    = roles.filter(r => !r.is_system).length;

  return (
    <div className="p-6 content-animate">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium
          ${toast.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
          {toast.type === 'success'
            ? <CheckCircle2 style={{ width: 16, height: 16 }} />
            : <AlertTriangle style={{ width: 16, height: 16 }} />
          }
          {toast.msg}
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display font-bold text-slate-800 text-xl flex items-center gap-2">
            <ShieldCheck style={{ width: 22, height: 22 }} className="text-indigo-600" />
            {t('rm.title')}
          </h2>
          <p className="text-sm text-slate-400 mt-0.5">
            {t('rm.subtitle')}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchRoles} disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-slate-600 bg-white border border-slate-200 hover:border-slate-300 rounded-xl transition-all">
            <RefreshCw style={{ width: 13, height: 13 }} className={loading ? 'animate-spin' : ''} />
            {t('common.refresh')}
          </button>
          <button onClick={() => navigate('/dashboard/users?create=1')}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-brand-200 hover:bg-brand-50 text-brand-700 text-sm font-semibold rounded-xl transition-all">
            <UsersIcon style={{ width: 15, height: 15 }} />
            {t('um.addUserBtn')}
          </button>
          <button onClick={() => setModal('add')}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl transition-all shadow-md">
            <Plus style={{ width: 15, height: 15 }} />
            {t('rm.createRoleBtn')}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm text-center">
          <div className="text-2xl font-bold text-indigo-600">{roles.length}</div>
          <div className="text-xs text-slate-400 font-medium mt-0.5">{t('rm.totalRoles')}</div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm text-center">
          <div className="text-2xl font-bold text-emerald-600">{customRoleCount}</div>
          <div className="text-xs text-slate-400 font-medium mt-0.5">{t('rm.customRoles')}</div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm text-center">
          <div className="text-2xl font-bold text-brand-600">{totalUsersWithRole}</div>
          <div className="text-xs text-slate-400 font-medium mt-0.5">{t('rm.totalUsers')}</div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-48">
          <Search style={{ width: 13, height: 13 }} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder={t('rm.searchPlaceholder')}
            className="w-full pl-8 pr-3 py-2 text-xs border border-slate-200 rounded-lg outline-none focus:border-brand-400" />
        </div>
      </div>

      {/* Roles Grid */}
      {loading ? (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 text-center text-slate-400">
          <RefreshCw style={{ width: 24, height: 24 }} className="animate-spin mx-auto mb-3" />
          {t('rm.loading')}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 text-center text-slate-400">
          <ShieldCheck style={{ width: 36, height: 36 }} className="mx-auto mb-3 opacity-30" />
          {t('rm.empty')}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map(r => (
            <div key={r.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center
                    ${r.is_system ? 'bg-blue-100' : 'bg-indigo-100'}`}>
                    {r.is_system
                      ? <Lock style={{ width: 18, height: 18 }} className="text-brand-600" />
                      : <ShieldCheck style={{ width: 18, height: 18 }} className="text-indigo-600" />
                    }
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-slate-800 text-base">{r.name}</h3>
                      {r.is_system && (
                        <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 bg-brand-50 text-brand-600 rounded">
                          {t('rm.systemBadge')}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {r.description || <span className="italic">{t('rm.noDesc')}</span>}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setModal({ edit: r })}
                    disabled={r.is_system}
                    title={r.is_system ? t('rm.tipSysNoEdit') : t('rm.tipEdit')}
                    className="p-1.5 hover:bg-brand-50 text-slate-400 hover:text-brand-600 rounded-lg transition-all
                      disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-slate-400">
                    <Edit2 style={{ width: 14, height: 14 }} />
                  </button>
                  <button onClick={() => setModal({ delete: r })}
                    disabled={r.is_system || r.user_count > 0}
                    title={
                      r.is_system ? t('rm.tipSysNoDelete')
                      : r.user_count > 0 ? t('rm.tipInUse', { n: r.user_count })
                      : t('rm.tipDelete')
                    }
                    className="p-1.5 hover:bg-red-50 text-slate-400 hover:text-red-600 rounded-lg transition-all
                      disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-slate-400">
                    <Trash2 style={{ width: 14, height: 14 }} />
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-3 mb-3 text-xs">
                <div className="flex items-center gap-1 text-slate-500">
                  <UsersIcon style={{ width: 12, height: 12 }} />
                  <span><strong className="text-slate-700">{r.user_count || 0}</strong> {t('rm.userUnit')}</span>
                </div>
                <div className="text-slate-300">·</div>
                <div className="text-slate-500">
                  <strong className="text-slate-700">{r.permissions.length}</strong> {t('rm.permUnit')}
                </div>
                {r.updated_at && (
                  <>
                    <div className="text-slate-300">·</div>
                    <div className="text-slate-400">{t('rm.updatedAt', { date: formatDate(r.updated_at, lang) })}</div>
                  </>
                )}
              </div>

              <div className="flex flex-wrap gap-1.5">
                {r.name === 'admin' ? (
                  <span className="text-xs bg-brand-50 border border-brand-200 px-2.5 py-1 rounded-lg text-brand-700 font-medium">
                    {t('rm.allAccessBuiltin')}
                  </span>
                ) : r.permissions.length === 0 ? (
                  <span className="text-xs italic text-slate-400">{t('rm.noPerm')}</span>
                ) : r.permissions.map(p => (
                  <span key={p} className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded-md font-medium">
                    {PERMISSION_LABEL[p] || prettyLabel(p)}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {modal === 'add' && (
        <RoleModal
          availablePerms={availablePerms}
          onSave={() => { setModal(null); fetchRoles(); showToast(t('rm.createdOk')); }}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.edit && (
        <RoleModal role={modal.edit}
          availablePerms={availablePerms}
          onSave={() => { setModal(null); fetchRoles(); showToast(t('rm.updatedOk')); }}
          onClose={() => setModal(null)}
        />
      )}

      {modal?.delete && createPortal(
        <div className="fixed inset-0 bg-black/40 z-[1000] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm border border-slate-100 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                <Trash2 style={{ width: 18, height: 18 }} className="text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">{t('rm.deleteRoleTitle')}</h3>
                <p className="text-xs text-slate-400">{t('rm.irreversible')}</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 mb-5">
              {t('rm.confirmDelete', { name: modal.delete.name })}
            </p>
            <div className="flex gap-2">
              <button onClick={() => setModal(null)}
                className="flex-1 py-2 text-sm text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-all">
                {t('common.cancel')}
              </button>
              <button onClick={() => handleDelete(modal.delete)}
                className="flex-1 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-xl transition-all">
                {t('rm.yesDelete')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
